import React, { useEffect } from 'react';
import { ArrowLeft, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function Terms() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#0B0F19] text-gray-300 py-20 px-6 max-w-4xl mx-auto">
      <button
        onClick={() => navigate('/')}
        className="flex items-center gap-2 text-[#E63946] hover:text-white transition-colors mb-12 font-bold group"
      >
        <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
        Back to Home
      </button>

      <div className="inline-flex items-center gap-2 bg-[#161B26] border border-gray-800 px-4 py-1.5 rounded-full text-sm font-semibold text-[#E63946] mb-6">
        <Shield className="w-4 h-4" /> Terms of Use
      </div>

      <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-8 tracking-tight">
        Terms <span className="text-[#E63946]">of Use</span>
      </h1>

      <div className="space-y-8 text-sm md:text-base leading-relaxed">

        <section>
          <h2 className="text-xl font-bold text-white mb-3">1. Introduction</h2>
          <p>
            Welcome to PFP AI. By downloading, installing, or using this application, you acknowledge that you have
            read, understood, and agreed to this Privacy Policy in its entirety. If you do not agree with any part
            of this policy, you must immediately stop using the App. Your continued use of the App constitutes
            your unconditional acceptance of all terms outlined herein.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-white mb-3">2. Information We Collect</h2>
          <p className="mb-2">When you use PFP AI, the following types of data may be collected:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Media Assets:</strong> Photos and videos you voluntarily upload for AI processing. You are solely responsible for ensuring you have the legal right to upload and process such content.</li>
            <li><strong>Usage Data:</strong> Information about how you interact with the App, including feature usage patterns and instructions submitted.</li>
            <li><strong>Device Information:</strong> Device model, operating system version, and unique device identifiers for operational purposes.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-bold text-white mb-3">3. User Responsibility for Content</h2>
          <p className="mb-3">
            <strong className="text-white">You are solely and entirely responsible for all content you upload, submit, or generate through the App.</strong> This includes, but is not limited to, photos, videos, text prompts, and any other media or data you introduce into the App.
          </p>
          <p className="mb-3">
            By using the App, you represent and warrant that:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>You own or have obtained all necessary rights, licenses, and permissions for every piece of content you upload.</li>
            <li>Your content does not infringe upon the intellectual property, privacy, or other rights of any third party.</li>
            <li>Your content does not violate any applicable local, national, or international law or regulation.</li>
            <li>You will not upload, generate, or distribute harmful, illegal, obscene, defamatory, threatening, or otherwise objectionable content through the App.</li>
            <li>Any consequences arising from content you upload or actions you take within the App are your sole responsibility.</li>
          </ul>
          <p className="mt-3">
            The App owner bears <strong className="text-white">no responsibility whatsoever</strong> for any content you submit, the legality of your actions, or any consequences that result from your use of the App.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-white mb-3">4. How We Use Your Information</h2>
          <p className="mb-2">Data collected is used exclusively to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Deliver and operate the AI photo and video editing features you request.</li>
            <li>Improve and refine AI model performance using anonymized, aggregated data.</li>
            <li>Communicate with you about updates, changes, or support matters.</li>
            <li>Maintain the security and integrity of the App and its services.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-bold text-white mb-3">5. Data Storage and Security</h2>
          <p>
            We apply industry-standard security measures to protect data within our systems. However,
            <strong className="text-white"> no method of electronic storage or transmission is 100% secure.</strong> Uploaded
            content is retained only for the duration of the processing session unless you explicitly opt into
            cloud save features. The App owner is not liable for any unauthorized access, data breaches, or
            loss of data beyond our reasonable control.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-white mb-3">6. Third-Party Services</h2>
          <p className="mb-3">
            The App integrates third-party AI processing providers and analytics tools. When your content is
            processed by these third-party services, their own terms of service and privacy policies apply
            independently of this policy.
          </p>
          <p>
            <strong className="text-white">The App owner is not responsible</strong> for how third-party services collect,
            store, process, or use your data. By using features that rely on third-party AI services, you
            expressly acknowledge and accept this. Any claims, losses, or disputes arising from third-party
            services are strictly between you and those providers.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-white mb-3">7. No Warranty &amp; Limitation of Liability</h2>
          <p className="mb-3">
            The App is provided <strong className="text-white">"AS IS"</strong> and <strong className="text-white">"AS AVAILABLE"</strong> without
            any warranty of any kind, either express or implied, including but not limited to fitness for a
            particular purpose, merchantability, or non-infringement.
          </p>
          <p className="mb-3">
            The App owner shall not be liable for:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Any direct, indirect, incidental, consequential, or punitive damages arising from your use of the App.</li>
            <li>Any loss of data, media, or content resulting from use or misuse of the App.</li>
            <li>Any harm or legal consequences resulting from content you upload or actions you take.</li>
            <li>Any interruptions, errors, defects, or outages in App availability or functionality.</li>
            <li>Any results, outputs, or AI-generated content that does not meet your expectations.</li>
            <li>Any actions taken by third-party service providers processing your data.</li>
          </ul>
          <p className="mt-3">
            <strong className="text-white">You assume all risk</strong> associated with your use of the App. By using PFP AI,
            you agree that the App owner's total liability to you, under any circumstances, shall not exceed
            the amount you paid for Premium Features (if any) in the 12 months preceding the claim.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-white mb-3">8. Indemnification</h2>
          <p>
            You agree to fully indemnify, defend, and hold harmless the App owner and any affiliates from
            and against all claims, liabilities, damages, losses, costs, and expenses (including reasonable
            legal fees) arising out of or related to: (a) your use or misuse of the App; (b) any content
            you upload or generate; (c) your violation of this Privacy Policy or any applicable law; or
            (d) your infringement of any third-party rights.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-white mb-3">9. Children's Privacy</h2>
          <p>
            The App is not intended for users under the age of 13. By using the App, you confirm you are
            at least 13 years old. If you are a parent or guardian and believe your child has submitted
            personal data, please contact us immediately. The App owner is not responsible for data submitted
            by users who misrepresent their age.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-white mb-3">10. Changes to This Policy</h2>
          <p>
            We reserve the right to update this Privacy Policy at any time. Material changes will be
            communicated via in-App notification or email at least 30 days before taking effect. Continued
            use of the App after the effective date constitutes your acceptance of the revised policy. It is
            your responsibility to review this policy periodically.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-white mb-3">11. Governing Law</h2>
          <p>
            This Privacy Policy is governed by the laws of India. Any disputes arising from this policy
            shall be subject to the exclusive jurisdiction of the courts of Chennai, India.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-white mb-3">12. Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy, please contact us at:
            <span className="text-[#E63946] ml-2 font-bold">svipes.office@gmail.com</span>
          </p>
        </section>

        <p className="pt-12 text-gray-500 italic text-xs">
          Last updated: May 15, 2026
        </p>
      </div>
    </div>
  );
}
